import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import Moment from 'moment-timezone';
import { extendMoment } from 'moment-range';
import _ from 'lodash';
import { setToast } from '../../store/ToastManagement';
import { DateInput, TimeInput } from 'semantic-ui-calendar-react';
import {
  Button,
  Divider,
  Grid,
  Header,
  Input,
  Segment,
  Table,
  Dropdown,
  Modal,
  Checkbox
} from 'semantic-ui-react';
import { servicesAPI, useCustomers } from '../../_services';
import { useCurrentUser } from '../../_services/hooks/user';
import { dbUTCDatetimeToLocal, localDateTimeToDBUTC } from '../../utils';

const Config = require('../../conf');
const moment = extendMoment(Moment);

export const ListLances = () => {
  const { currentUser, currentUserCan } = useCurrentUser();

  const customers = useCustomers();

  const [types, setTypes] = useState([]);
  const [customerToLances, setCustomerToLances] = useState([]);
  const [orionTicketId, setOrionTicketId] = useState(null);
  const [orionTicketTagId, setOrionTicketTagId] = useState(null);
  const [serialNumberFilter, setSerialNumberFilter] = useState('');
  const [customerNameFilter, setCustomerNameFilter] = useState('');
  const [localAuthorityFilter, setLocalAuthorityFilter] = useState('');
  const [hideInactive, setHideInactive] = useState(false);
  const [hideTestAccounts, setHideTestAccounts] = useState(false);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('desc');
  const [sortLabel, setSortLabel] = useState('descending');
  const [sortedData, setSortedData] = useState([]);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [customerId, setCustomerId] = useState(null);
  const [lanceId, setLanceId] = useState(null);
  const [lance, setLance] = useState(null);
  const [charging, setCharging] = useState(false);
  const [testCustomer, setTestCustomer] = useState(null);
  const [customerCurrentLanceIds, setCustomerCurrentLanceIds] = useState([]);
  const [fromDate, setFromDate] = useState(null);
  const [fromTime, setFromTime] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [toTime, setToTime] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [refreshLances, setRefreshLances] = useState(false);
  const [refreshOrion, setRefreshOrion] = useState(false);

  const dispatch = useDispatch();
  const displayMessage = (title, message, success) =>
    dispatch(
      setToast({
        title: title,
        success: success,
        body: message
      })
    );

  const sortTable = column => {
    setSortColumn(column);
    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    setSortLabel(sortLabel === 'ascending' ? 'descending' : 'ascending');
  };

  useEffect(() => {
    servicesAPI.AdminServices('getOrionTypes').then(types => setTypes(types));

    sortTable('created_datetime');
    setSortDirection('desc');
    setSortLabel('descending');
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!customers.length) return;

    servicesAPI
      .CustomerServices('getLanceAssignments')
      .then(customerToLances => {
        servicesAPI.InventoryServices('getLances').then(lances => {
          setCustomerToLances(
            customerToLances.map(customerToLance => {
              const customer = customers.find(
                c => c.id === customerToLance.customer_id
              );
              const lance = lances.find(
                l => l.tag_id === customerToLance.lance_tag_id
              );

              const name = customer
                ? customer.forename + ' ' + customer.surname
                : 'Unknown';
              const test_account = customer ? customer.test_account : false;
              return {
                ...customerToLance,
                programmable_id: lance ? lance.programmable_id : '',
                lance_version: lance ? lance.lance_version : '',
                customer_name: name,
                test_account: test_account
              };
            })
          );
        });
      });
  }, [customers, refreshLances]);

  useEffect(() => {
    if (customers.length === 0 || customerToLances.length === 0) return;

    const sorted =
      sortColumn && sortDirection
        ? _.orderBy(customerToLances, sortColumn, sortDirection)
        : customerToLances;

    setSortedData(
      sorted
        .filter(
          ({ lance_serial_number }) =>
            !serialNumberFilter ||
            lance_serial_number
              .toLowerCase()
              .includes(serialNumberFilter.toLowerCase())
        )
        .filter(
          ({ customer_name }) =>
            !customerNameFilter ||
            (customer_name &&
              customer_name
                .toLowerCase()
                .includes(customerNameFilter.toLowerCase()))
        )
        .filter(
          ({ local_authority }) =>
            !localAuthorityFilter ||
            (local_authority &&
              local_authority
                .toLowerCase()
                .includes(localAuthorityFilter.toLowerCase()))
        )
        .filter(({ test_account }) => !hideTestAccounts || !test_account)
        .filter(({ status }) => !hideInactive || status === 'ACTIVE')
    );
  }, [
    customers,
    customerToLances,
    serialNumberFilter,
    customerNameFilter,
    localAuthorityFilter,
    hideTestAccounts,
    hideInactive,
    sortColumn,
    sortDirection
  ]);

  useEffect(() => {
    if (customers.length === 0 || customerToLances.length === 0) return;

    const customer = customerId
      ? customers.find(c => c.id === customerId)
      : null;
    const customerToLance = lanceId
      ? customerToLances.find(l => l.lance_tag_id === lanceId)
      : null;

    if (!customer) {
      setTestCustomer(false);
      setCustomerCurrentLanceIds([]);
    }

    if (!customerToLance) {
      setFromDate(moment().format('YYYY-MM-DD'));
      setFromTime(moment().format('HH:mm'));
      setToDate(moment().format('YYYY-MM-DD'));
      setToTime(moment().format('HH:mm'));
    }

    if (!customer || !customerToLance) return;

    if (lance?.tag_id !== lanceId) {
      if (lanceId) {
        servicesAPI
          .InventoryServices('getLance', lanceId)
          .then(lance => setLance(lance));
      } else {
        setLance(null);
      }
    }

    setTestCustomer(customer.test_account);
    const customerLances = customerToLances.filter(
      l => l.customer_id === customerId && l.status === 'ACTIVE'
    );
    setCustomerCurrentLanceIds(
      customerLances ? customerLances.map(l => l.lance_tag_id) : []
    );

    if (customer.id === customerToLance.customer_id) {
      // Remove lance
      const from = customerToLance.from_datetime
        ? moment
            .utc(customerToLance.from_datetime.replace('T', ' ').split(',')[0])
            .local()
        : null;
      setFromDate(from.format('YYYY-MM-DD'));
      setFromTime(from.format('HH:mm'));

      setToDate(moment().format('YYYY-MM-DD'));
      setToTime(moment().format('HH:mm'));
    } else {
      // Assign lance
      setFromDate(moment().format('YYYY-MM-DD'));
      setFromTime(moment().format('HH:mm'));

      setToDate(null);
      setToTime(null);
    }
  }, [customers, customerToLances, customerId, lanceId]); // eslint-disable-line

  useEffect(() => {
    if (!customerId) {
      setOrionTicketId(null);
      setOrionTicketTagId(null);
      return;
    }

    // Find associated Orion tickets
    servicesAPI
      .AdminServices('getOrionsByCustomer', customerId)
      .then(orions => {
        const lanceAssignmentOrions = orions.filter(o => {
          // // Ignore old tickets
          // const diffDays = moment
          //   .utc()
          //   .diff(
          //     moment.utc(o.created_datetime, 'YYYY-MM-DDTHH:mm:ss,Z'),
          //     'days'
          //   );
          // if (diffDays > 14) return false;

          // const status = types.statuses.find(t => t.id === o.status_id);
          const event_type = types.event_types.find(
            t => t.id === o.event_type_id
          );
          // const ticket_type = types.ticket_types.find(t => t.id === o.ticket_type_id);
          return event_type?.name === 'Lance Assignment';
        });

        const ticketId = lanceAssignmentOrions.length
          ? lanceAssignmentOrions[0].ticket_number
          : null;
        const ticketTagId = lanceAssignmentOrions.length
          ? lanceAssignmentOrions[0].id
          : null;

        setOrionTicketId(ticketId);
        setOrionTicketTagId(ticketTagId);
      });
  }, [customerId, refreshOrion]); // eslint-disable-line

  const assignLanceToCustomer = () => {
    setIsSubmitting(true);

    const values = {
      from_datetime: localDateTimeToDBUTC(fromDate, fromTime),
      to_datetime: localDateTimeToDBUTC(toDate, toTime)
    };
    servicesAPI
      .CustomerServices('assignLanceToCustomer', values, lanceId, customerId)
      .then(response => {
        setAssignModalOpen(false);
        setIsSubmitting(false);
        displayMessage('Success!', 'Lance successfully asigned.', true);

        // Ensure lance is unblocked
        const values = { status: 'Active' };
        servicesAPI.InventoryServices('updateLance', values, lanceId);

        servicesAPI.AdminServices(
          'auditUserActivity',
          {
            entity_type: 'LANCE',
            entity_id: lanceId,
            activity: 'LanceAssignment',
            notes: `Lance ${lanceId} assigned to customer ${customerId}`,
            email_subject: '',
            email_body: ''
          },
          currentUser.id
        );

        // Refresh data
        setRefreshLances(!refreshLances);
      })
      .catch(e => {
        setIsSubmitting(false);
        displayMessage('Error', e, false);
      });
  };

  const removeLanceFromCustomer = () => {
    setIsSubmitting(true);

    const values = {
      to_datetime: localDateTimeToDBUTC(toDate, toTime)
    };
    servicesAPI
      .CustomerServices('removeLanceFromCustomer', values, lanceId, customerId)
      .then(response => {
        setAssignModalOpen(false);
        setIsSubmitting(false);
        displayMessage('Success!', 'Lance successfully removed.', true);
        servicesAPI.AdminServices(
          'auditUserActivity',
          {
            entity_type: 'LANCE',
            entity_id: lanceId,
            activity: 'LanceRemoval',
            notes: `Lance ${lanceId} removed from customer ${customerId}`,
            email_subject: '',
            email_body: ''
          },
          currentUser.id
        );

        // Refresh data
        setRefreshLances(!refreshLances);
      })
      .catch(e => {
        setIsSubmitting(false);
        displayMessage('Error', e, false);
      });
  };

  const blockLance = lance => {
    setIsSubmitting(true);

    const values = { status: 'Blocked' };

    servicesAPI.InventoryServices('updateLance', values, lance.tag_id).then(
      response => {
        lance.status = 'Blocked';
        setLance(lance);

        setAssignModalOpen(false);
        setIsSubmitting(false);
        displayMessage('Success!', 'Lance blocked.', true);
        servicesAPI.AdminServices(
          'auditUserActivity',
          {
            entity_type: 'LANCE',
            entity_id: lanceId,
            activity: 'LanceBlocked',
            notes: `Lance ${lanceId} blocked for customer ${customerId}`,
            email_subject: '',
            email_body: ''
          },
          currentUser.id
        );

        // Refresh data
        setRefreshLances(!refreshLances);
      },
      e => {
        setIsSubmitting(false);
        displayMessage('Error', e, false);
      }
    );
  };

  const unblockLance = lance => {
    setIsSubmitting(true);

    const values = { status: 'Active' };

    servicesAPI.InventoryServices('updateLance', values, lance.tag_id).then(
      response => {
        lance.status = 'Active';
        setLance(lance);

        setAssignModalOpen(false);
        setIsSubmitting(false);
        displayMessage('Success!', 'Lance unblocked.', true);
        servicesAPI.AdminServices(
          'auditUserActivity',
          {
            entity_type: 'LANCE',
            entity_id: lanceId,
            activity: 'LanceUnblocked',
            notes: `Lance ${lanceId} unblocked for customer ${customerId}`,
            email_subject: '',
            email_body: ''
          },
          currentUser.id
        );

        // Refresh data
        setRefreshLances(!refreshLances);
      },
      e => {
        setIsSubmitting(false);
        displayMessage('Error', e, false);
      }
    );
  };

  return (
    <Segment>
      <Grid stretched>
        <Grid.Row>
          <Grid.Column>
            <Header as="h2">Lances</Header>
          </Grid.Column>
          <Grid.Column
            floated="right"
            textAlign="right"
            mobile={6}
            tablet={4}
            computer={3}
          >
            {/*<Button
              onClick={() => {
                setCustomerId(null);
                setLanceId(null);
                setCharging(false);
                setAssignModalOpen(true);
              }}
              color="olive"
              disabled={!currentUserCan('ManageLance')}
            >
              Assign / Unassign Lance
            </Button>*/}
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column mobile={8} tablet={6} computer={4}>
            <Input
              type="text"
              placeholder="filter serial number"
              icon={{
                name: 'close',
                link: true,
                onClick: () => {
                  setSerialNumberFilter('');
                }
              }}
              value={serialNumberFilter}
              onChange={(event, { value }) => {
                setSerialNumberFilter(value);
              }}
            />
          </Grid.Column>
          <Grid.Column mobile={8} tablet={6} computer={4}>
            <Input
              type="text"
              placeholder="filter customer name"
              icon={{
                name: 'close',
                link: true,
                onClick: () => {
                  setCustomerNameFilter('');
                }
              }}
              value={customerNameFilter}
              onChange={(event, { value }) => {
                setCustomerNameFilter(value);
              }}
            />
          </Grid.Column>
          <Grid.Column mobile={8} tablet={6} computer={4}>
            <Input
              type="text"
              placeholder="filter local authority"
              icon={{
                name: 'close',
                link: true,
                onClick: () => {
                  setLocalAuthorityFilter('');
                }
              }}
              value={localAuthorityFilter}
              onChange={(event, { value }) => {
                setLocalAuthorityFilter(value);
              }}
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Checkbox
            toggle
            label="Hide inactive lances"
            checked={hideInactive}
            onChange={e => setHideInactive(!hideInactive)}
          />
          <Checkbox
            toggle
            label="Hide test accounts"
            checked={hideTestAccounts}
            onChange={e => setHideTestAccounts(!hideTestAccounts)}
          />
        </Grid.Row>
      </Grid>
      <Divider hidden />
      <Grid>
        <Table sortable celled fixed>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell
                sorted={sortColumn === 'lance_serial_number' ? sortLabel : null}
                onClick={() => sortTable('lance_serial_number')}
                style={{ width: '100px' }}
              >
                Serial No.
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={sortColumn === 'programmable_id' ? sortLabel : null}
                onClick={() => sortTable('programmable_id')}
                style={{ width: '210px' }}
              >
                Programmable ID
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={sortColumn === 'lance_version' ? sortLabel : null}
                onClick={() => sortTable('lance_version')}
                style={{ width: '70px' }}
              >
                Version
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={sortColumn === 'customer_name' ? sortLabel : null}
                onClick={() => sortTable('customer_name')}
                style={{ width: '200px' }}
              >
                Assigned To Customer
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={sortColumn === 'status' ? sortLabel : null}
                onClick={() => sortTable('status')}
                style={{ width: '90px' }}
              >
                Status
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={sortColumn === 'local_authority' ? sortLabel : null}
                onClick={() => sortTable('local_authority')}
                style={{ width: '100px' }}
              >
                Local
                <br />
                Authority
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={sortColumn === 'from_datetime' ? sortLabel : null}
                onClick={() => sortTable('from_datetime')}
                style={{ width: '120px' }}
              >
                Assigned
                <br />
                Date/Time
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={sortColumn === 'to_datetime' ? sortLabel : null}
                onClick={() => sortTable('to_datetime')}
                style={{ width: '120px' }}
              >
                Unassigned
                <br />
                Date/Time
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={sortColumn === 'activated_datetime' ? sortLabel : null}
                onClick={() => sortTable('activated_datetime')}
                style={{ width: '120px' }}
              >
                Activated
                <br />
                Date/Time
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {sortedData.map((d, idx) => (
              <Table.Row
                key={idx}
                onClick={() => {
                  // Get all active sessions for this customer
                  servicesAPI
                    .MeteringServices(
                      'getSessionsByLanceToCustomerIds',
                      {
                        ids: [d.id]
                      },
                      1,
                      100,
                      true
                    )
                    .then(activeSessions => {
                      const filteredSessions = activeSessions.filter(
                        s => s.lance_customer_id === d.id
                      );
                      setCustomerId(d.customer_id);
                      setLanceId(d.lance_tag_id);
                      setCharging(filteredSessions.length > 0);
                      setAssignModalOpen(true);
                    });
                }}
              >
                <Table.Cell>{d.lance_serial_number}</Table.Cell>
                <Table.Cell>{d.programmable_id}</Table.Cell>
                <Table.Cell>{d.lance_version}</Table.Cell>
                <Table.Cell>
                  {d.customer_name}
                  {d.test_account ? ' (TEST ACCOUNT)' : ''}
                </Table.Cell>
                <Table.Cell>{d.status}</Table.Cell>
                <Table.Cell>{d.local_authority}</Table.Cell>
                <Table.Cell>{dbUTCDatetimeToLocal(d.from_datetime)}</Table.Cell>
                <Table.Cell>{dbUTCDatetimeToLocal(d.to_datetime)}</Table.Cell>
                <Table.Cell>
                  {dbUTCDatetimeToLocal(d.activated_datetime)}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </Grid>
      <Modal
        closeIcon
        onClose={() => setAssignModalOpen(false)}
        open={assignModalOpen}
        size="mini"
        style={{ padding: '20px' }}
      >
        <Modal.Content>
          <Header as="h2" textAlign="left">
            Lance Management
          </Header>
          <Header sub color="grey" as="h3" style={{ marginBottom: '10px' }}>
            Customer
          </Header>
          <Dropdown
            selection
            search
            placeholder="Enter customer name"
            options={customers
              .filter(c => c.id === customerId || c.status === 'Active')
              .map(c => ({
                key: c.id,
                text: c.forename + ' ' + c.surname,
                value: c.id
              }))
              .sort((c1, c2) => c1.text.localeCompare(c2.text))}
            onChange={(e, { value }) => setCustomerId(value)}
            value={customerId}
          />
          <Header sub color="grey" as="h3" style={{ marginBottom: '10px' }}>
            Lance
          </Header>
          <Dropdown
            selection
            search
            placeholder="Enter lance serial number"
            options={customerToLances
              .filter(l => {
                // Only show the customer's Lance or unassigned Lances
                return !l.customer_id || l.customer_id === customerId;
              })
              .map(l => ({
                key: l.lance_tag_id,
                text: l.lance_serial_number,
                value: l.lance_tag_id
              }))
              .sort((l1, l2) => l1.text.localeCompare(l2.text))}
            onChange={(e, { value }) => {
              setLanceId(value);
              setOrionTicketId(null);
              setOrionTicketTagId(null);
            }}
            value={lanceId}
          />
          {customerId && lanceId && (
            <>
              <Header sub color="grey" as="h3" style={{ marginBottom: '10px' }}>
                Assigned Date/Time
              </Header>
              <DateInput
                name="fromDate"
                placeholder="Assigned date"
                value={fromDate}
                dateFormat="YYYY-MM-DD"
                onChange={(e, { value }) => {
                  setFromDate(value);
                }}
                animation={false}
                style={{ width: 150 }}
                disabled={customerCurrentLanceIds.includes(lanceId)}
              />
              <TimeInput
                name="fromTime"
                placeholder="00:00"
                value={fromTime}
                onChange={(e, { value }) => {
                  setFromTime(value);
                }}
                style={{ width: 150 }}
                disabled={customerCurrentLanceIds.includes(lanceId)}
              />
              {customerCurrentLanceIds.includes(lanceId) && (
                <>
                  <Header
                    sub
                    color="grey"
                    as="h3"
                    style={{ marginBottom: '10px' }}
                  >
                    Unassigned Date & Time
                  </Header>
                  <DateInput
                    name="toDate"
                    placeholder="Unassigned date"
                    value={toDate}
                    dateFormat="YYYY-MM-DD"
                    onChange={(e, { value }) => {
                      setToDate(value);
                      setToTime(moment().format('HH:mm'));
                    }}
                    minDate={fromDate}
                    animation={false}
                    style={{ width: 150 }}
                  />
                  <TimeInput
                    name="toTime"
                    placeholder="00:00"
                    value={toTime}
                    onChange={(e, { value }) => {
                      setToTime(value);
                    }}
                    style={{ width: 150 }}
                  />
                </>
              )}
            </>
          )}
        </Modal.Content>
        {customerId && lanceId && !testCustomer && (
          <>
            {orionTicketId ? (
              <>
                Associated Orion ticket:{' '}
                <a
                  href={`${
                    Config.Local().orion_url
                  }/orion/ticket/${orionTicketTagId}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {orionTicketId}
                </a>
              </>
            ) : (
              <>
                No ORION "Lance Assignment" ticket found for this customer.{' '}
                <a
                  href={`${Config.Local().orion_url}/orion/tickets/create`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Create ORION ticket
                </a>
                .
                <br />
                <br />
                <Button
                  color="olive"
                  onClick={() => setRefreshOrion(!refreshOrion)}
                >
                  Recheck ORION
                </Button>
                <br />
              </>
            )}
            <br />
            <br />
          </>
        )}
        {customerId && lanceId && (
          <>
            {!customerCurrentLanceIds.includes(lanceId) && (
              <>
                <Button
                  color="olive"
                  onClick={() => assignLanceToCustomer()}
                  loading={isSubmitting}
                  disabled={
                    !currentUserCan('ManageLance') ||
                    isSubmitting ||
                    (!testCustomer && !orionTicketId) ||
                    !customerId ||
                    !lanceId ||
                    !fromDate ||
                    !fromTime
                  }
                >
                  Assign New Lance To Customer
                </Button>
                <br />
                <br />
              </>
            )}
            {charging ? (
              <>
                <div style={{ color: 'red' }}>
                  Lance is actively charging. Cannot unassign
                </div>
                <br />
              </>
            ) : (
              ''
            )}
            {customerCurrentLanceIds.includes(lanceId) && (
              <>
                <Button
                  color="olive"
                  onClick={() => removeLanceFromCustomer()}
                  loading={isSubmitting}
                  disabled={
                    !currentUserCan('ManageLance') ||
                    isSubmitting ||
                    (!testCustomer && !orionTicketId) ||
                    !customerId ||
                    !lanceId ||
                    charging
                  }
                >
                  Unassign Current Lance From Customer
                </Button>
                <br />
                <br />
                <Button
                  color={lance?.status === 'Blocked' ? 'green' : 'red'}
                  onClick={() => {
                    if (lance.status === 'Blocked') unblockLance(lance);
                    else blockLance(lance);
                  }}
                  loading={isSubmitting}
                  disabled={
                    !currentUserCan('ManageLance') ||
                    isSubmitting ||
                    !customerId ||
                    !lanceId ||
                    charging
                  }
                >
                  {lance?.status === 'Blocked'
                    ? 'Unblock Lance To Permit Charging'
                    : 'Block Lance To Prevent Charging'}
                </Button>
                <br />
                <br />
              </>
            )}
          </>
        )}
        <Button onClick={() => setAssignModalOpen(false)}>Cancel</Button>
      </Modal>
    </Segment>
  );
};
